import rateAlertsVisuals from "../../media/rateAlertsVisuals";

const rateAlerts = {
  "id": "rate-alerts",
  "slug": "rate-alerts",
  "category": "Applications",
  "navTitle": "Introducing Rate Alerts",
  "title": "Introducing Rate Alerts",
  "introduction": [
    "Many XE customers checked exchange rates repeatedly while waiting for the right time to travel, transfer money, or make a financial decision. I led the introduction of Rate Alerts so users could set a target rate and receive a notification when the market reached it.",
    "The feature gave customers a practical reason to return to the app and created a more direct connection between monitoring a rate and using XE’s money transfer service."
  ],
  "challenge": [
    {
      "title": "Repeated manual checking",
      "paragraphs": [
        "Customers were returning to the app several times a day to watch specific currencies. They needed a simpler way to follow the market without checking constantly."
      ]
    },
    {
      "title": "Keeping the feature easy to use",
      "paragraphs": [
        "Rate Alerts involved several choices: currency pair, target rate, notification settings, and account registration. The experience needed to remain quick and understandable."
      ]
    },
    {
      "title": "Supporting business goals without disrupting the task",
      "paragraphs": [
        "The feature could increase registration and support money transfer, but those opportunities had to feel relevant to the customer’s reason for creating the alert."
      ]
    },
    {
      "title": "Standing out in a competitive market",
      "paragraphs": [
        "Similar features were beginning to appear in other currency apps. XE needed an implementation that was useful, accessible, and consistent with the simplicity customers already expected."
      ]
    }
  ],
  "process": [
    {
      "title": "Understanding how customers tracked rates",
      "paragraphs": [
        "I used research and competitor analysis to understand when people monitored exchange rates, what triggered action, and how they expected an alert to work."
      ],
      "bullets": [
        "Conducted interviews and surveys with customers who checked rates regularly.",
        "Identified common use cases, including travel, recurring payments, and international money transfers.",
        "Reviewed competing products to understand common patterns and areas of friction.",
        "Defined the minimum information needed to create and manage an alert.",
        "Used the findings to shape the feature requirements and initial interaction model."
      ],
      "closing": "This helped keep the feature focused on a clear customer need."
    },
    {
      "title": "Making alerts quick to create and manage",
      "paragraphs": [
        "I explored several interaction models and tested how easily users could choose a target rate, understand the direction of the market, and edit an existing alert."
      ],
      "bullets": [
        "Designed and tested different ways to enter or adjust a target rate.",
        "Supported both direct numeric input and touch-based controls.",
        "Simplified the setup flow so users could create an alert with as few decisions as possible.",
        "Designed clear states for active, triggered, paused, and expired alerts.",
        "Included keyboard, screen-reader, contrast, and touch-target requirements in the design."
      ],
      "closing": "The final experience allowed experienced users to move quickly while still being understandable to people less familiar with exchange rates."
    },
    {
      "title": "Connecting alerts to the wider XE experience",
      "paragraphs": [
        "I worked with product and engineering teams to connect notifications, account registration, and money transfer without turning the feature into a sales flow."
      ],
      "bullets": [
        "Collaborated with mobile developers on real-time notifications and alert management.",
        "Connected triggered alerts to the relevant currency pair and current market information.",
        "Introduced registration at points where it supported saving and managing alerts across sessions.",
        "Created a relevant path to money transfer when a customer’s target rate was reached.",
        "Used analytics, customer feedback, and app-store reviews to refine the experience after launch."
      ],
      "closing": "The connection to money transfer followed the customer’s intent rather than interrupting the alert task."
    }
  ],
  "outcomes": [
    {
      "title": "Strong customer adoption",
      "description": "More than two million Rate Alerts were created each month, making the feature an important part of ongoing app use."
    },
    {
      "title": "Increased engagement",
      "description": "Customers returned to create, review, and update alerts instead of using the app only for one-time conversion."
    },
    {
      "title": "More registrations",
      "description": "Saving and managing alerts gave customers a clear reason to create an XE account."
    },
    {
      "title": "Support for money transfer",
      "description": "Triggered alerts created a timely path into XE’s money transfer service for customers who were already waiting to act."
    },
    {
      "title": "Product differentiation",
      "description": "Rate Alerts strengthened the app’s value beyond basic currency conversion and helped XE remain competitive as similar features entered the market."
    }
  ],
  "reflection": [
    "Rate Alerts worked because the feature addressed a behaviour customers already had. They were watching the market and waiting for a particular rate. The product made that easier.",
    "The main design challenge was keeping the setup simple while supporting different levels of knowledge and several possible next steps. Research and post-launch data helped us add the right amount of guidance without slowing down customers who already knew what they wanted to do."
  ],
  "media": {
    "blocks": [
      {
        "id": "rate-alerts-flow",
        "type": "carousel",
        "placement": "afterIntro",
        "items": rateAlertsVisuals,
        "layout": "mobileFocus",
        "options": {
          "frameMaxHeight": "min(64vh, 720px)"
        }
      }
    ]
  }
};

export default rateAlerts;
